import { Link, useNavigate } from 'react-router-dom'
import { Archive, CheckCircle2, Circle, Clock, CornerDownRight, ExternalLink, Play, Repeat, RotateCcw, Sparkles, Sun, Trash2 } from 'lucide-react'
import type { Task } from '../../services/taskService'
import { PRIORITY_LABEL, durationText, lifeAreaLabel, whenMeta } from '../../utils/taskForm'
import { actionItemIdOf, parentTaskIdOf } from '../../utils/taskSections'
import { planTask } from '../../utils/planTask'
import { ActionMenu } from '../ui/ActionMenu'
import { Timestamp } from '../ui/Timestamp'
import { useToast } from '../ui/Toast'
import { PlanItMenu } from './PlanItMenu'
import { TaskScoreRing } from './TaskScoreRing'

/**
 * One row of the task list (redesign 2026-09-05). Built for scanning: a mark
 * you can tap to finish, a title you can read on a phone, and one quiet line
 * that says when it is due, how long it takes and what it sits under. The
 * primary verbs stay put — the mark finishes it, Plan it gives it a time —
 * and everything else lives under ⋯. Quick actions (action items) render as
 * rows here too; their ids ride along in __actionItemId. They are steps,
 * not a second kind of thing (owner 2026-09-06), so the row wears no tag
 * and opens the task it belongs to.
 */
interface Props {
  task: Task
  today: Date
  /** Say what it sits under (grouped by when / life area). */
  showParent?: boolean
  /** Say its life area (grouped by goal). */
  showArea?: boolean
  personaName: string
  diaryMode?: boolean
  onDone: (task: Task) => void | Promise<void>
  onReopen: (task: Task) => void | Promise<void>
  onDelete: (task: Task) => void
  onChanged: () => void | Promise<void>
  /** Under a time sort, say the time the rows are ordered by. */
  timeHint?: 'created' | 'updated' | null
}

export function TaskRow({ task, today, showParent = false, showArea = false, personaName, diaryMode = false, onDone, onReopen, onDelete, onChanged, timeHint = null }: Props) {
  const navigate = useNavigate()
  const toast = useToast()
  const aid = actionItemIdOf(task)
  // An action item is a step, not a thing with its own page (owner 2026-09-06):
  // the row opens the task it belongs to. Steps with no parent are not links.
  const parentId = parentTaskIdOf(task)
  const href = aid ? (parentId ? `/tasks/${parentId}` : '/tasks') : `/tasks/${task.id}`
  const done = task.status === 'completed' || task.status === 'cancelled'
  const lapsed = task.status === 'lapsed'
  const open = !done && !lapsed
  const important = open && (task.priority === 'urgent' || task.priority === 'high')
  const when = open ? whenMeta(task, today) : null
  const est = durationText(task.estimated_duration)
  const sessions = Boolean(task.sessions_target && task.sessions_target > 0)
  const accrues = open && (Boolean(task.incremental) || sessions)
  const pct = sessions
    ? Math.min(100, Math.round(((task.sessions_done || 0) / (task.sessions_target as number)) * 100))
    : Math.min(100, task.progress || 0)
  const ringLabel = sessions ? `${task.sessions_done || 0}/${task.sessions_target}` : `${pct}%`
  const area = task.life_area || task.parent?.life_area || null

  const plan = (target: 'today' | 'pick' | 'place' | 'backlog') => async () => {
    try {
      const r = await planTask(task, target, navigate)
      if (r) { toast.toast({ variant: 'success', title: r.title, description: r.description }); await onChanged() }
    } catch (err) {
      toast.error('Could not plan it', err instanceof Error ? err.message : 'Please try again.')
    }
  }
  const menuItems = [
    // Below lg the row has no room for a Plan it control; its four
    // destinations sit at the top of ⋯ instead.
    ...(open && !aid ? [
      { label: 'Today', icon: <Sun className="h-4 w-4" />, onSelect: plan('today'), className: 'lg:hidden' },
      { label: 'Pick a time', icon: <Clock className="h-4 w-4" />, onSelect: plan('pick'), className: 'lg:hidden' },
      { label: `Let ${personaName} place it`, icon: <Sparkles className="h-4 w-4" />, onSelect: plan('place'), className: 'lg:hidden' },
      { label: 'Backlog', icon: <Archive className="h-4 w-4" />, onSelect: plan('backlog'), className: 'lg:hidden border-b border-soft rounded-b-none mb-1' },
    ] : []),
    { label: 'Open', icon: <ExternalLink className="h-4 w-4" />, onSelect: () => navigate(href), testId: 'task-row-open' },
    ...(done || lapsed ? [{ label: 'Reopen', icon: <RotateCcw className="h-4 w-4" />, onSelect: () => void onReopen(task), testId: 'task-row-reopen' }] : []),
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => onDelete(task), danger: true, testId: 'task-row-delete' },
  ]

  const mark = open ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); void onDone(task) }}
      aria-label={`Mark done: ${task.title}`}
      title="Mark done"
      data-testid={aid ? 'action-item-row-done' : 'task-row-done'}
      className={`group/mark inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-ok/10 lg:h-8 lg:w-8 ${task.status === 'in_progress' ? 'text-brand' : 'text-soft'} hover:text-ok`}
    >
      {task.status === 'in_progress'
        ? <Play className="h-4 w-4 fill-current group-hover/mark:hidden" aria-hidden="true" />
        : <Circle className="h-5 w-5 group-hover/mark:hidden" aria-hidden="true" />}
      <CheckCircle2 className="hidden h-5 w-5 group-hover/mark:block" aria-hidden="true" />
    </button>
  ) : (
    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center lg:h-8 lg:w-8" aria-hidden="true">
      {lapsed ? <Clock className="h-5 w-5 text-warn/70" /> : <CheckCircle2 className="h-5 w-5 text-ok" />}
    </span>
  )

  return (
    <div
      className={`group flex items-center gap-1.5 rounded-lg border px-1.5 py-1 transition-colors sm:gap-2 sm:px-2 ${
        diaryMode ? 'border-warn bg-warn/20 hover:bg-warn/30' : 'border-soft bg-surface hover:border-brand'
      } ${!open ? 'task-row-done-state' : ''}`}
      onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button,[role=menu],[role=dialog]')) navigate(href) }}
      data-testid="task-row"
      data-status={task.status}
    >
      {mark}

      <div className="min-w-0 flex-1 py-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <Link
            to={href}
            onClick={(e) => e.stopPropagation()}
            className={`truncate font-semibold hover:text-brand ${open ? 'text-primary' : 'text-muted'} ${diaryMode ? 'font-handwritten text-base sm:text-lg' : 'text-app-body'}`}
            title={task.description || undefined}
            data-testid="task-row-title"
          >
            {task.title}
          </Link>
          {important && (
            <span className={`hidden shrink-0 rounded px-1.5 py-0.5 text-app-micro font-medium sm:inline ${task.priority === 'urgent' ? 'bg-bad/15 text-bad' : 'bg-warn/15 text-warn'}`}>
              {PRIORITY_LABEL[task.priority] || task.priority}
            </span>
          )}
          {task.is_recurring && <Repeat className="h-3.5 w-3.5 shrink-0 text-muted" aria-label="Repeats" />}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-x-2 overflow-hidden whitespace-nowrap text-app-micro text-muted sm:gap-x-2.5" data-testid="task-row-meta">
          {important && <span className={`shrink-0 font-medium sm:hidden ${task.priority === 'urgent' ? 'text-bad' : 'text-warn'}`}>{PRIORITY_LABEL[task.priority]}</span>}
          {when && <span className={`shrink-0 ${when.tone}`} data-testid="task-row-when">{when.text}</span>}
          {task.status === 'in_progress' && task.started_at && <span className="shrink-0 text-brand">started <Timestamp value={task.started_at} mode="datetime" /></span>}
          {done && <span className="shrink-0">{task.status === 'cancelled' ? 'dropped' : 'done'} <Timestamp value={task.finished_at || task.completion_date || task.updated_at} mode="datetime" /></span>}
          {lapsed && (
            <span className="shrink-0">
              let go <Timestamp value={task.lapsed_at} mode="datetime" />
              {' · '}<Link to={href} onClick={(e) => e.stopPropagation()} className="text-brand hover:underline">How did it go?</Link>
            </span>
          )}
          {open && est && <span className="shrink-0">{est}</span>}
          {open && !aid && task.task_score?.scored && task.task_score.missing.length > 0 && (
            <TaskScoreRing score={task.task_score} size={12} caption captionClass="text-app-micro" className="hidden shrink-0 sm:inline-flex" />
          )}
          {showParent && task.parent?.title && (
            <span className="hidden min-w-0 items-center gap-1 truncate md:inline-flex" title={task.parent.title}>
              <CornerDownRight className="h-3 w-3 shrink-0" aria-hidden="true" />{task.parent.title}
            </span>
          )}
          {showArea && area && <span className="hidden shrink-0 md:inline">{lifeAreaLabel(area)}</span>}
          {timeHint && <span className="shrink-0" data-testid="task-row-time-hint">{timeHint === 'created' ? 'added' : 'updated'} <Timestamp value={timeHint === 'created' ? (task.created_at || task.updated_at) : (task.updated_at || task.created_at)} mode="datetime" /></span>}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        {accrues && (
          <div className="relative mr-0.5 h-7 w-7" title={sessions ? `${ringLabel} sessions` : `${pct}% done`}>
            <svg className="h-7 w-7 -rotate-90" viewBox="0 0 28 28">
              <circle cx="14" cy="14" r="11" stroke="currentColor" strokeWidth="2.5" fill="none" className="text-soft" />
              <circle cx="14" cy="14" r="11" stroke="currentColor" strokeWidth="2.5" fill="none" strokeDasharray={`${(pct * 69.1) / 100} 69.1`} strokeLinecap="round" className="text-brand" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-app-nano font-medium text-secondary">{ringLabel}</span>
          </div>
        )}
        {open && !aid && (
          <PlanItMenu
            task={task}
            compact
            onChanged={onChanged}
            // Unplanned rows keep Plan it in view — it is their next step; a
            // planned row shows it on hover or focus so the list stays quiet.
            className={when?.planned ? 'hidden lg:block lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100 lg:focus-within:opacity-100' : 'hidden lg:block'}
          />
        )}
        <ActionMenu compact label={`More for ${task.title}`} items={menuItems} testId="task-row-more" />
      </div>
    </div>
  )
}

export default TaskRow
